'use client';

import { useState, JSX, ChangeEvent, useEffect } from 'react';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Unstable_Grid2';
import { toast } from 'react-toastify';

import { type PoolType } from '@/types';
import DatePickers from '../date-pickers';
import SelectFactoryContract from './SelectFactoryContract';
import { isCheckInteger } from '@/lib/BigNumbers';
import { FormControlLabel, FormHelperText, Switch } from '@mui/material';
import Header from '../Header';
import { readCoinType } from '@/api/asset/coin-type/read';
import { ResCoinType, ResCoinTypeAddLabel } from '@/types/coin-type/read';
import { StatusSelect } from '@/lib/filter/stakingPoolState';

export function PoolSettingForm({
  pools,
  setPools,
  title,
  type,
}: {
  pools: PoolType;
  setPools: (value: any) => void;
  title: string;
  type: 'create' | 'detail';
}): JSX.Element {
  const [symbolList, setSymbolList] = useState<ResCoinTypeAddLabel[]>([]);
  useEffect(() => {
    (async () => {
      const list = await readCoinType();
      const data: ResCoinTypeAddLabel[] = list.data.map((item: ResCoinType) => ({
        ...item,
        label: `${item.symbol}(${item.network})`,
      }));
      setSymbolList(data);
    })();

    return () => {};
  }, []);

  const handleChangeValue = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (!name) {
      return;
    }
    if (['minUsdt', 'maxUsdt', 'goalUsdt'].includes(name)) {
      const isCheck = isCheckInteger(value);
      if (!isCheck) {
        toast.error('소수점을 사용할 수 없습니다.');
        return;
      }
    }
    setPools((prev: PoolType) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleChangeSelect = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    if (!name || !value) {
      return;
    }
    setPools((prev: PoolType) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSwitch = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setPools((prev: PoolType) => ({
      ...prev,
      [name]: Number(checked),
    }));
  };

  return (
    <>
      <Card>
        <Header title={title} />
        <Divider />
        <CardContent>
          <Grid container spacing={3}>
            {type === 'create' && (
              <>
                <Grid xs={12}>
                  <Typography variant="h4">팩토리 풀 설정</Typography>
                </Grid>
                <Grid xs={12} sm={12}>
                  <SelectFactoryContract setPools={setPools} />
                </Grid>
              </>
            )}

            <Grid xs={12}>
              <Typography variant="h4">상세 설정</Typography>
            </Grid>

            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>제목</InputLabel>
                <OutlinedInput value={pools.title} label="title" name="title" onChange={handleChangeValue} />
              </FormControl>
            </Grid>

            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Symbol</InputLabel>
                <Select
                  label="Symbol"
                  name="symbol"
                  variant="outlined"
                  value={pools.symbol}
                  onChange={handleChangeSelect}
                >
                  {symbolList &&
                    symbolList.map((option: ResCoinTypeAddLabel) => (
                      <MenuItem key={option.symbol} value={option.symbol}>
                        {option.label}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid xs={12} sm={6}>
              <DatePickers
                label="모금 시작일"
                setValue={setPools}
                poolName="waitStartDt"
                defaultValue={pools.waitStartDt}
              />
            </Grid>
            <Grid xs={12} sm={6}>
              <DatePickers
                label="모금 종료일"
                setValue={setPools}
                poolName="waitExpireDt"
                defaultValue={pools.waitExpireDt}
              />
            </Grid>
            <Grid xs={12} sm={6}>
              <DatePickers label="풀 시작일" setValue={setPools} poolName="startDt" defaultValue={pools.startDt} />
            </Grid>
            <Grid xs={12} sm={6}>
              <DatePickers label="풀 종료일" setValue={setPools} poolName="endDt" defaultValue={pools.endDt} />
            </Grid>

            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>사용자 최소 스테이킹 금액 ($)</InputLabel>
                <OutlinedInput
                  value={pools.minUsdt}
                  inputProps={{
                    type: 'number', // 숫자만 입력받도록 타입을 지정합니다.
                    inputMode: 'numeric', // 숫자 키패드를 표시할 수 있도록 지정합니다.
                    pattern: '[0-9]*', // 숫자만 입력받도록 패턴을 설정합니다.
                  }}
                  placeholder="usdt"
                  label="minUsdt"
                  name="minUsdt"
                  onChange={handleChangeValue}
                />
              </FormControl>
            </Grid>

            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>사용자 최대 스테이킹 금액($)</InputLabel>
                <OutlinedInput
                  value={pools.maxUsdt}
                  placeholder="usdt"
                  label="maxUsdt"
                  name="maxUsdt"
                  onChange={handleChangeValue}
                />
                <FormHelperText>※ 0으로 설정시 최대값 없음</FormHelperText>
              </FormControl>
            </Grid>

            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>풀 목표 금액 ($)</InputLabel>
                <OutlinedInput
                  value={pools.goalUsdt}
                  placeholder="usdt"
                  label="goalUsdt"
                  name="goalUsdt"
                  onChange={handleChangeValue}
                />
              </FormControl>
            </Grid>

            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>풀 최대 금액 ($)</InputLabel>
                <OutlinedInput
                  value={pools.goalMaxUsdt}
                  placeholder="usdt"
                  label="goalMaxUsdt"
                  name="goalMaxUsdt"
                  onChange={handleChangeValue}
                />
              </FormControl>
            </Grid>

            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>이자율 %</InputLabel>
                <OutlinedInput
                  value={pools.interestRate}
                  inputProps={{
                    type: 'number', // 숫자만 입력받도록 타입을 지정합니다.
                    inputMode: 'numeric', // 숫자 키패드를 표시할 수 있도록 지정합니다.
                    pattern: '[0-9]*', // 숫자만 입력받도록 패턴을 설정합니다.
                  }}
                  label="interestRate"
                  name="interestRate"
                  onChange={handleChangeValue}
                />
              </FormControl>
            </Grid>

            {type === 'detail' && (
              <Grid md={6} xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>State</InputLabel>
                  <Select
                    label="state"
                    name="state"
                    variant="outlined"
                    value={String(pools.state)}
                    onChange={handleChangeSelect}
                  >
                    {StatusSelect.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid md={6} xs={12}>
              <FormControl fullWidth>
                <InputLabel>프라이빗 토큰 심볼(퍼블릭이면 빈 공란)</InputLabel>
                <OutlinedInput
                  value={pools.privateTokenSymbol}
                  placeholder="퍼블릭이면 빈 공란"
                  label="privateTokenSymbol"
                  name="privateTokenSymbol"
                  onChange={handleChangeValue}
                />
              </FormControl>
            </Grid>

            <Grid md={6} xs={12}>
              <FormControl fullWidth>
                <InputLabel>프라이빗 토큰 컨트랙트(퍼블릭이면 빈 공란)</InputLabel>
                <OutlinedInput
                  value={pools.extraContractAddr}
                  placeholder="퍼블릭이면 빈 공란"
                  label="extraContractAddr"
                  name="extraContractAddr"
                  onChange={handleChangeValue}
                />
              </FormControl>
            </Grid>

            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>등록한 지갑 주소</InputLabel>
                <OutlinedInput value={pools.address} label="address" name="address" onChange={handleChangeValue} />
              </FormControl>
            </Grid>

            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>컨트랙트 주소</InputLabel>
                <OutlinedInput
                  value={pools.contractAddr}
                  label="contractAddr"
                  name="contractAddr"
                  onChange={handleChangeValue}
                />
              </FormControl>
            </Grid>

            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <FormControlLabel control={<Switch onChange={handleSwitch} />} label="풀 숨김" name="isHide" />
              </FormControl>
            </Grid>

            <Grid xs={12}>
              <InputLabel>풀 설명</InputLabel>
              <OutlinedInput
                value={pools.description}
                placeholder="풀에 대한 설명"
                multiline
                fullWidth
                rows={4}
                label="description"
                name="description"
                onChange={handleChangeValue}
              />
            </Grid>

            <Grid xs={12}>
              <InputLabel>메모 (관리자 전용 메모)</InputLabel>
              <OutlinedInput
                value={pools.memo}
                placeholder="관리자 전용 메모(사용자 화면X)"
                multiline
                fullWidth
                rows={4}
                label="memo"
                name="memo"
                onChange={handleChangeValue}
              />
            </Grid>
          </Grid>
        </CardContent>
        <Divider />
        <CardActions sx={{ justifyContent: 'flex-end' }}>
          {/* <Button type="submit" variant="contained">
            Create
          </Button> */}
        </CardActions>
      </Card>
    </>
  );
}
